// Motor de cálculo de depreciación de activos fijos colombianos. Funciones
// puras (sin UI, sin `state`): reciben el activo y parámetros, devuelven un
// desglose. Depreciación en línea recta: (costo - valor residual) / vida útil
// en meses, con tope para no depreciar por debajo del valor residual. Se
// calculan en paralelo el valor CONTABLE (NIIF para Pymes, Sección 17 — vida
// útil económica real) y el FISCAL (tasa máxima Art. 137 E.T. + Decreto
// 1625/2016), ya que pueden diferir: el asiento contable siempre usa el valor
// contable; el fiscal queda registrado en el kardex para una futura
// conciliación fiscal / cálculo de impuesto diferido (fuera de alcance hoy).

function round(n) {
  return Math.round(Number(n) || 0);
}

// Activos cuyo costo de adquisición sea igual o menor a 50 UVT pueden
// depreciarse en su totalidad en el año de compra (Art. 1.2.1.18.5, Decreto
// 1625 de 2016 — "activos de menor cuantía").
export function esActivoMenorCuantia(costoAdquisicion, uvtValor) {
  return (Number(costoAdquisicion) || 0) <= 50 * (Number(uvtValor) || 0);
}

function acumuladaPrevia(activo, campo) {
  const kardex = activo.depreciaciones || [];
  return kardex.length === 0 ? 0 : (kardex[kardex.length - 1][campo] || 0);
}

// Cuota de depreciación en línea recta de UN mes, con tope en el valor
// residual (nunca deprecia más de lo que falta para llegar a él).
function depreciacionMensualLineaRecta(costo, valorResidual, vidaUtilMeses, previa) {
  if (!vidaUtilMeses || vidaUtilMeses <= 0) return 0;
  const baseDepreciable = Math.max(0, costo - valorResidual);
  const cuotaMensual = baseDepreciable / vidaUtilMeses;
  const restante = Math.max(0, baseDepreciable - previa);
  return round(Math.min(cuotaMensual, restante));
}

export function calcularDepreciacionMensual(activo, fecha) {
  const costo = Number(activo.costo_adquisicion) || 0;
  const valorResidual = Number(activo.valor_residual) || 0;

  const acumContablePrevia = acumuladaPrevia(activo, "depreciacionAcumuladaContable");
  const acumFiscalPrevia = acumuladaPrevia(activo, "depreciacionAcumuladaFiscal");

  const depreciacionContable = depreciacionMensualLineaRecta(
    costo, valorResidual, activo.vida_util_contable_meses, acumContablePrevia
  );
  let depreciacionFiscal = depreciacionMensualLineaRecta(
    costo, valorResidual, activo.vida_util_fiscal_meses, acumFiscalPrevia
  );

  // Depreciación acelerada por turnos adicionales (Art. 140 E.T.): +25% sobre
  // la alícuota fiscal si el bien se usa 16h diarias — no aplica a inmuebles.
  if (activo.turnos_adicionales && activo.categoria !== "terreno" && activo.categoria !== "construccion") {
    const restanteFiscal = Math.max(0, (costo - valorResidual) - acumFiscalPrevia);
    depreciacionFiscal = round(Math.min(depreciacionFiscal * 1.25, restanteFiscal));
  }

  const depreciacionAcumuladaContable = acumContablePrevia + depreciacionContable;
  const depreciacionAcumuladaFiscal = acumFiscalPrevia + depreciacionFiscal;
  const valorEnLibros = Math.max(valorResidual, costo - depreciacionAcumuladaContable);

  return {
    fecha,
    depreciacionContable, depreciacionFiscal,
    depreciacionAcumuladaContable, depreciacionAcumuladaFiscal,
    valorEnLibros
  };
}

// Orquestador: si el activo califica como de menor cuantía y el usuario eligió
// depreciarlo así, lo deprecia 100% de una sola vez; si no, corre la
// depreciación mensual normal. Devuelve el registro listo para el kardex.
export function liquidarDepreciacionActivo(activo, fecha, uvtValor) {
  const yaDepreciado = (activo.depreciaciones || []).length > 0;
  const costo = Number(activo.costo_adquisicion) || 0;
  const valorResidual = Number(activo.valor_residual) || 0;

  if (activo.depreciar_menor_cuantia && esActivoMenorCuantia(costo, uvtValor) && !yaDepreciado) {
    const baseDepreciable = Math.max(0, costo - valorResidual);
    return {
      fecha,
      depreciacionContable: baseDepreciable, depreciacionFiscal: baseDepreciable,
      depreciacionAcumuladaContable: baseDepreciable, depreciacionAcumuladaFiscal: baseDepreciable,
      valorEnLibros: valorResidual,
      origen: "menor_cuantia"
    };
  }

  return { ...calcularDepreciacionMensual(activo, fecha), origen: "mensual" };
}

// Utilidad o pérdida al dar de baja un activo (venta, retiro, pérdida):
// compara el valor de venta (0 si no hubo venta) contra el valor en libros a
// la fecha de la baja.
export function calcularBajaActivo(activo, valorVenta) {
  const costo = Number(activo.costo_adquisicion) || 0;
  const valorResidual = Number(activo.valor_residual) || 0;
  const depreciacionAcumuladaContable = acumuladaPrevia(activo, "depreciacionAcumuladaContable");
  const valorEnLibros = Math.max(valorResidual, costo - depreciacionAcumuladaContable);
  const venta = Number(valorVenta) || 0;
  const utilidadPerdida = venta - valorEnLibros;

  return { valorEnLibros, depreciacionAcumuladaContable, valorVenta: venta, utilidadPerdida, esUtilidad: utilidadPerdida >= 0 };
}
